import posthog from 'posthog-js'
import type { PostHogConfig } from 'posthog-js'

import { sanitize } from './sanitize'

const key = import.meta.env?.VITE_APP_POSTHOG_KEY ?? ''
const host =
  import.meta.env?.VITE_APP_POSTHOG_HOST ?? 'https://eu.i.posthog.com'
const uiHost = import.meta.env?.VITE_APP_POSTHOG_UI_HOST ?? ''

/**
 * True only when a project API key is set. When false nothing is initialised
 * and every helper in this folder is a no-op, so local dev and CI produce zero
 * network traffic and zero console noise. A missing key is a supported state,
 * not a broken one.
 */
export const isAnalyticsConfigured = Boolean(key)

/**
 * Whether to call `posthog.group()`. Group analytics is a paid PostHog add-on;
 * while this is off the organization dimension travels as event properties
 * (`org_id`, `industry`, `company_size`) and org-level uniqueness is computed
 * with a SQL insight instead.
 */
export const isGroupAnalyticsEnabled =
  import.meta.env?.VITE_APP_POSTHOG_GROUPS === 'true'

/**
 * account-ui tags its build env as `account-development` / `account-production`
 * so its ClickUp support tickets stay isolated (see `src/features/tickets`).
 * Analytics wants the bare name, so strip the prefix — same treatment as
 * `toUnleashEnvironment` in `src/feature-flags/config.ts`.
 */
export const toAnalyticsEnvironment = (appEnv: string | undefined): string =>
  (appEnv ?? 'development').replace(/^account-/, '')

export const analyticsEnvironment = toAnalyticsEnvironment(
  import.meta.env?.VITE_APP_ENV
)

const options: Partial<PostHogConfig> = {
  api_host: host,
  // Only meaningful behind a reverse proxy, where the ingest host is ours and
  // the SDK would otherwise build toolbar links against it.
  ui_host: uiHost || undefined,

  // Pinned to a dated snapshot so an SDK upgrade cannot change capture
  // behaviour silently. Bump deliberately, never as a side effect of `npm up`.
  defaults: '2026-06-25',

  // Anonymous visitors get no person profile until `identify` runs, which keeps
  // the person count (and the bill) tied to real accounts. The pre-signup
  // session is still stitched onto the person afterwards via `$anon_distinct_id`.
  person_profiles: 'identified_only',

  // OFF deliberately. Autocapture records the text of clicked elements, which
  // on the Done screen is the workspace name — that is exactly what BR-44
  // forbids. It is also DOM-scale volume on an admin UI, against a budget of
  // roughly 40k events a month.
  autocapture: false,

  // The project has heatmaps enabled, and the SDK adopts that from remote
  // config unless told otherwise. Same volume objection as autocapture.
  enable_heatmaps: false,

  // Also switched on in the project, and also adopted from remote config: web
  // vitals started arriving as `$web_vitals` the moment remote config was
  // restored. Real volume on every page load, measuring page speed rather than
  // onboarding — outside the boundary this iteration committed to.
  capture_performance: false,

  capture_pageview: 'history_change',
  capture_pageleave: true,

  // Replay is started per surface by `useSessionReplay`, not globally.
  // Recording the whole admin app would spend the free tier's recording quota
  // on screens no one will watch.
  disable_session_recording: true,
  session_recording: {
    // No exceptions: every input in the onboarding wizard is identifying —
    // workspace name, slug, invitee addresses, the free-text "other" answers.
    maskAllInputs: true
  },

  // Feature flags stay on Unleash (`src/feature-flags`), so PostHog must not
  // evaluate any — but the `/flags` request itself has to keep happening.
  //
  // NOT `advanced_disable_flags`. That kills the request outright, and with it
  // **remote configuration** — which is what authorises session replay. Set that
  // way, `startSessionRecording()` is accepted and silently records nothing:
  // the console says "[RemoteConfig] Remote config is disabled" and zero
  // recordings ever appear. This narrower option keeps the config payload and
  // skips only the flag evaluation, which is the part we genuinely don't want.
  advanced_disable_feature_flags: true,
  disable_surveys: true,

  before_send: sanitize,
  property_denylist: ['$ip', 'email', 'name', 'first_name', 'last_name']
}

/**
 * Guards against a second `init`. React StrictMode renders twice and Vite's HMR
 * re-executes modules; PostHog warns and rebuilds its state on a repeat init.
 */
let started = false

/**
 * Initialises PostHog. Called at module scope by `AnalyticsProvider` so it runs
 * before React mounts — see the comment there for why that matters.
 */
export const startAnalytics = (): void => {
  if (started || !isAnalyticsConfigured) return

  started = true
  posthog.init(key, options)
}
